import { useState, useEffect } from "react";
import { useTranslation } from "@wix/fe-essentials-standalone";
import suggestions from "../../../../public/data/suggestions.json";
import { isMobile } from "@wix/picasso-common";
import { useBiLogger } from "../../../bi/useBiLogger";

export interface Suggestion {
  key: string;
  translationKey: string;
  prompt: string;
}

// Number of suggestions to show at once
const SUGGESTIONS_TO_SHOW = 4;
const MOBILE_SUGGESTIONS_TO_SHOW = 3;

export const useSuggestions = (
  onSelectPrompt: (prompt: string) => void,
): {
  currentSuggestions: Suggestion[];
  handleRegenerateClick: () => void;
  handleSuggestionClick: (suggestion: Suggestion) => void;
  t: (key: string) => string;
} => {
  const { t } = useTranslation();
  const biLogger = useBiLogger();
  const [currentSuggestions, setCurrentSuggestions] = useState<Suggestion[]>(
    [],
  );
  const [suggestionsToShow, setSuggestionsToShow] = useState(
    isMobile() ? MOBILE_SUGGESTIONS_TO_SHOW : SUGGESTIONS_TO_SHOW,
  );

  const getRandomSuggestions = () => {
    if (suggestions.length === 0) return [];
    const shuffled = [...suggestions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, suggestionsToShow);
  };

  useEffect(() => {
    const handleResize = () =>
      setSuggestionsToShow(isMobile() ? 3 : SUGGESTIONS_TO_SHOW);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (suggestions.length > 0) {
      setCurrentSuggestions(getRandomSuggestions());
    }
  }, [suggestions, suggestionsToShow]);

  const handleRegenerateClick = () => {
    biLogger.trackUserClickPicasso({ buttonName: "Regenerate Suggestions" });
    setCurrentSuggestions(getRandomSuggestions());
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    biLogger.trackUserClickPicasso({
      buttonName: `Prompt Suggestion Selected - ${suggestion.key}`,
    });
    onSelectPrompt(t(suggestion.prompt));
  };

  return {
    currentSuggestions,
    handleRegenerateClick,
    handleSuggestionClick,
    t,
  };
};
